import * as THREE from 'three';
import { TWEEN } from '/node_modules/three/examples/jsm/libs/tween.module.min.js';
import RoomObjectAbstract from '../room-object.abstract';
import { MONITOR_TYPE } from '../../data/room-config';
import { MONITOR_PARTS_WITHOUT_BUTTONS, MONITOR_PART_TYPE, MONITOR_SCREEN_BUTTONS } from './data/monitor-data';
import { MONITOR_ARM_MOUNT_CONFIG, MONITOR_BUTTONS_CONFIG, MONITOR_CONFIG, MONITOR_LINKS_CONFIG } from './data/monitor-config';
import { HELP_ARROW_TYPE } from '../../shared/help-arrows/help-arrows-config';
import HelpArrows from '../../shared/help-arrows/help-arrows';
import Loader from '../../../../core/loader';
import { SPARKLE_CONFIG } from '../../shared/sparkle-shaders/sparkle-config';
import sparkleVertexShader from '../../shared/sparkle-shaders/sparkle-vertex.glsl';
import sparkleFragmentShader from '../../shared/sparkle-shaders/sparkle-fragment.glsl';
import VolumeIcon from './volume-icon/volume-icon';
import { CAMERA_CONFIG } from '../../camera-controller/data/camera-config';
import { CAMERA_MODE, CAMERA_FOCUS_OBJECT_TYPE } from '../../camera-controller/data/camera-data';
import { Black } from 'black-engine';

// ORYN iframe overlay — tracker writes position/size/scale every frame.
const ORYN_IFRAME_BASE_STYLE = `
  position: fixed; border: 0; background: transparent;
  z-index: 50; opacity: 0; transition: opacity 0.35s ease;
  pointer-events: auto; overflow: hidden;
  transform-origin: top left;
  box-shadow: 0 6px 30px rgba(0,0,0,0.35);
  border-radius: 6px;
`;
// Tuning: how much of the screen rect the ORYN "window" occupies.
// Bigger top inset = more menu-bar visible. Bigger bottom inset = more dock visible.
const ORYN_INSET = {
  top: 0.08,     // taller — small slice for menu bar
  bottom: 0.10,  // dock still peeks
  left: 0.05,
  right: 0.05,
};
// Logical render width for the iframe (ORYN renders at this width, then scales
// visually to fit the inset rect). Higher = denser content; lower = bigger UI.
const ORYN_LOGICAL_WIDTH = 1280;
import Materials from '../../../../core/materials';
import SCENE_CONFIG from '../../../../core/configs/scene-config';

export default class Monitor extends RoomObjectAbstract {
  constructor(meshesGroup, roomObjectType, audioListener) {
    super(meshesGroup, roomObjectType, audioListener);

    this._screenGroup = null;
    this._arrowsTween = null;
    this._helpArrows = null;
    this._screenTexture = null;
    this._showreelTexture = null;
    this._showreelVideoElement = null;
    this._volumeIcon = null;
    this._focusObject = null;
    this._sceneRenderTexture = null;

    this._plane = new THREE.Plane();
    this._pNormal = new THREE.Vector3(0, 1, 0);
    this._shift = new THREE.Vector3();
    this._currentPositionZ = 0;
    this._previousPositionZ = 0;

    this._isMountSelected = false;
    this._isShowreelPlaying = false;
    this._isShowreelPaused = false;
    this._isGameActive = false;
    this._isScreenActiveForGame = false;
    this._isOrynActive = false;
    this._orynIframe = null;
    this._isAllObjectsInteractionEnabled = false;

    this._init();
  }

  update(dt) {
    MONITOR_SCREEN_BUTTONS.forEach((partType) => {
      const part = this._parts[partType];
      part.material.uniforms.uTime.value += dt;
    });

    if (this._currentPositionZ === this._previousPositionZ) {
      return;
    }

    const monitor = this._parts[MONITOR_PART_TYPE.Monitor];
    const deltaZ = this._currentPositionZ - monitor.userData.startPosition.z;
    monitor.position.z = this._currentPositionZ;

    this._updateMonitorPosition(deltaZ);
    this._updateArmMount(deltaZ);
    this._updateHelpArrows(deltaZ);

    this._previousPositionZ = this._currentPositionZ;
  }

  onClick(intersect, onPointerDownClick) {
    if (!this._isInputEnabled) {
      return;
    }

    const roomObject = intersect.object;
    const partType = roomObject.userData.partType;
    let isObjectDraggable = false;

    if (MONITOR_PARTS_WITHOUT_BUTTONS.includes(partType)) {
      isObjectDraggable = true;
      this._onMonitorClick(intersect);
      Black.engine.containerElement.style.cursor = 'grabbing';

      if (SCENE_CONFIG.isMobile) {
        this._helpArrows.show();
      }
    }

    if (onPointerDownClick === false && MONITOR_SCREEN_BUTTONS.includes(partType)) {
      this._onButtonsClick(partType);
    }

    if (onPointerDownClick === false && partType === MONITOR_PART_TYPE.MonitorScreen) {
      if (this._isScreenActiveForGame) {
        this.events.post('onMonitorScreenClickForGame');
      } else {
        this.events.post('onMonitorScreenClick');
      }
    }

    if (onPointerDownClick === false && partType === MONITOR_PART_TYPE.MonitorCloseFocusIcon) {
      this.events.post('onCloseFocusIconClick')
    }

    return isObjectDraggable;
  }

  onAllObjectsInteraction() {
    if (this._isAllObjectsInteractionEnabled) {
      if (this._isShowreelPlaying) {
        this._stopShowreel();

        return;
      }

      if (this._isGameActive) {
        this._hideGame();

        return;
      }

      // Intro tour previously randomly auto-played either the (now-dead)
      // showreel video or the Transfer It game. Both have been removed from
      // the public lineup — leave the monitor showing its icon grid instead.
    }
  }

  onPointerMove(raycaster) {
    if (!this._isMountSelected) {
      return;
    }

    const planeIntersect = new THREE.Vector3();

    raycaster.ray.intersectPlane(this._plane, planeIntersect);
    const monitor = this._parts[MONITOR_PART_TYPE.Monitor];
    const startPositionZ = monitor.userData.startPosition.z;

    this._currentPositionZ = planeIntersect.z + this._shift.z;
    this._currentPositionZ = THREE.MathUtils.clamp(this._currentPositionZ, MONITOR_CONFIG.minZ + startPositionZ, MONITOR_CONFIG.maxZ + startPositionZ);

    this._updatePosition();
  }

  onPointerOver(intersect) {
    if (this._isPointerOver || SCENE_CONFIG.isMobile) {
      return;
    }

    super.onPointerOver();

    const partType = intersect.object.userData.partType;

    if (MONITOR_PARTS_WITHOUT_BUTTONS.includes(partType) && CAMERA_CONFIG.mode === CAMERA_MODE.OrbitControls) {
      this._helpArrows.show();
      Black.engine.containerElement.style.cursor = 'grab';
    }

    if (partType === MONITOR_PART_TYPE.MonitorScreen) {
      if (this._isScreenActiveForGame) {
        Black.engine.containerElement.style.cursor = 'pointer';
      } else {
        Black.engine.containerElement.style.cursor = 'zoom-in';
      }
    }
  }

  onPointerUp() {
    Black.engine.containerElement.style.cursor = 'grab';

    if (SCENE_CONFIG.isMobile) {
      this._helpArrows.hide();
    }
  }

  onPointerOut() {
    if (!this._isPointerOver) {
      return;
    }

    super.onPointerOut();

    this._helpArrows.hide();
  }

  onLeftKeyClick(buttonType) {
    if (buttonType !== null && MONITOR_SCREEN_BUTTONS.includes(buttonType)) {
      this._onButtonsClick(buttonType);
    }

    if (this._isGameActive) {
      this.events.post('onMonitorScreenClickForGame');
    }
  }

  onButtonOver(buttonType) {
    this._clearButtonsColor();

    const button = this._parts[buttonType];
    button.material.uniforms.uColor.value = MONITOR_BUTTONS_CONFIG.mouseOverColor;
  }

  onButtonOut() {
    this._clearButtonsColor();
  }

  stopShowreelVideo() {
    if (this._isShowreelPlaying) {
      this._stopShowreel();
    }
  }

  stopGame() {
    if (this._isGameActive) {
      this._hideGame();
    }
  }

  pauseShowreelVideo() {
    if (this._isShowreelPlaying) {
      if (this._isShowreelPaused) {
        this._resumeShowreel();
      } else {
        this._pauseShowreel();
      }
    }
  }

  isShowreelPlaying() {
    return this._isShowreelPlaying;
  }

  getMeshesForOutline(mesh) {
    const partType = mesh.userData.partType;

    if (MONITOR_PARTS_WITHOUT_BUTTONS.includes(partType)) {
      return this._getPartsWithoutButtons();
    }

    if (MONITOR_SCREEN_BUTTONS.includes(partType)) {
      return [mesh];
    }

    if (partType === MONITOR_PART_TYPE.MonitorScreen) {
      return [mesh];
    }

    if (partType === MONITOR_PART_TYPE.MonitorCloseFocusIcon) {
      return [mesh];
    }
  }

  getScreen() {
    return this._parts[MONITOR_PART_TYPE.MonitorScreen];
  }

  setScreenActive() {
    this._parts[MONITOR_PART_TYPE.MonitorScreen].userData.isActive = true;
  }

  setScreenInactive() {
    this._parts[MONITOR_PART_TYPE.MonitorScreen].userData.isActive = false;
    // Auto-close any embedded iframe overlay so it doesn't float outside the screen.
    this._hideOrynOverlay();
  }

  setScreenActiveForGame() {
    this._isScreenActiveForGame = true;
    this._parts[MONITOR_PART_TYPE.MonitorScreen].userData.hideOutline = true;
  }

  setScreenInactiveForGame() {
    this._isScreenActiveForGame = false;
    this._parts[MONITOR_PART_TYPE.MonitorScreen].userData.hideOutline = false;
  }

  onVolumeChanged(volume, showIcon = true) {
    if (showIcon) {
      this._volumeIcon.onVolumeChanged(volume);
    }
  }

  enableSound() {
    this._volumeIcon.enableSound();
  }

  disableSound() {
    this._volumeIcon.disableSound();
  }

  getFocusPosition() {
    const monitorGlobalPosition = new THREE.Vector3();
    this._focusObject.getWorldPosition(monitorGlobalPosition);

    return monitorGlobalPosition;
  }

  showCloseFocusIcon() {
    const closeFocusIcon = this._parts[MONITOR_PART_TYPE.MonitorCloseFocusIcon];
    closeFocusIcon.visible = true;
    closeFocusIcon.scale.set(0, 0, 0);

    if (this._closeFocusIconTween) {
      this._closeFocusIconTween.stop();
    }

    this._closeFocusIconTween = new TWEEN.Tween(closeFocusIcon.scale)
      .to({ x: 1, y: 1, z: 1 }, 200)
      .easing(TWEEN.Easing.Back.Out)
      .delay(100)
      .start();
  }

  hideCloseFocusIcon() {
    const closeFocusIcon = this._parts[MONITOR_PART_TYPE.MonitorCloseFocusIcon];

    if (this._closeFocusIconTween) {
      this._closeFocusIconTween.stop();
    }

    this._closeFocusIconTween = new TWEEN.Tween(closeFocusIcon.scale)
      .to({ x: 0, y: 0, z: 0 }, 200)
      .easing(TWEEN.Easing.Back.In)
      .start()
      .onComplete(() => {
        closeFocusIcon.visible = false;
      });
  }

  addMonitorScreenTexture(texture) {
    this._sceneRenderTexture = texture;
  }

  getMeshesForOutlinePreview() {
    const monitor = this._parts[MONITOR_PART_TYPE.Monitor];
    const monitorArmMountBase = this._parts[MONITOR_PART_TYPE.MonitorArmMountBase];
    const monitorArmMountArm01 = this._parts[MONITOR_PART_TYPE.MonitorArmMountArm01];
    const monitorArmMountArm02 = this._parts[MONITOR_PART_TYPE.MonitorArmMountArm02];

    return [monitor, monitorArmMountBase, monitorArmMountArm01, monitorArmMountArm02];
  }

  enableAllObjectsInteraction() {
    this._isAllObjectsInteractionEnabled = true;
  }

  disableAllObjectsInteraction() {
    this._isAllObjectsInteractionEnabled = false;
  }

  resetToInitState() {
    if (this._isShowreelPlaying) {
      this._stopShowreel();
    }

    if (this._isGameActive) {
      this._hideGame();
    }

    this._onPositionChanged(0);
    this._updatePosition();
  }

  _clearButtonsColor() {
    MONITOR_SCREEN_BUTTONS.forEach((partType) => {
      const button = this._parts[partType];
      button.material.uniforms.uColor.value = new THREE.Color(0xffffff);
    });
  }

  _onMonitorClick(intersect) {
    this._isMountSelected = true;
    const pIntersect = new THREE.Vector3().copy(intersect.point);
    this._plane.setFromNormalAndCoplanarPoint(this._pNormal, pIntersect);
    this._shift.subVectors(this._parts[MONITOR_PART_TYPE.Monitor].position, intersect.point);
  }

  _onButtonsClick(partType) {
    this._isMountSelected = false;

    if (!this._isShowreelPlaying && !this._isGameActive && !this._isOrynActive) {
      // Showreel slot (top-left) -> ORYN, embedded in screen.
      // Pop-out CTA on the overlay opens crm-oryn.web.app in a new tab for sign-in.
      if (partType === MONITOR_PART_TYPE.MonitorScreenShowreelIcon) {
        window.track && window.track('monitor_oryn_opened');
        this._openInScreen(MONITOR_LINKS_CONFIG.showreel, {
          label: 'enlarge ⛶',
          trackEvent: 'monitor_oryn_popout',
        });
      }

      // Top-middle slot (formerly Transfer It mesh, now displays Game Boy icon)
      // -> Game Boy site. Moved up here so the visible top row reads as
      // "ORYN · Game Boy · DOCK".
      if (partType === MONITOR_PART_TYPE.MonitorScreenTransferItIcon) {
        window.track && window.track('monitor_gameboy_opened');
        this._openInScreen(MONITOR_LINKS_CONFIG.gameBoy, {
          label: 'enlarge ⛶',
          trackEvent: 'monitor_gameboy_popout',
        });
      }

      // Top-right slot (formerly GameBoy mesh, now displays DOCK icon)
      // -> SpaceX ISS Docking Sim. Moved up from the bottom row so all three
      // top icons are visible at the same eye level.
      if (partType === MONITOR_PART_TYPE.MonitorScreenGameBoyIcon) {
        window.track && window.track('monitor_iss_opened');
        this._openInScreen(MONITOR_LINKS_CONFIG.crazyPumpkin, {
          label: 'enlarge ⛶',
          trackEvent: 'monitor_iss_popout',
        });
      }

      // Bottom-left slot (CrazyPumpkin mesh) is now hidden — DOCK moved up.
      // Click handler intentionally left empty.

      // Bottom-middle slot (CubeScape mesh) was NASA Eyes — removed. The
      // monitor now ends at the top row with 3 focused icons.
    }

    if (partType === MONITOR_PART_TYPE.MonitorScreenCloseIcon) {
      if (this._isOrynActive) {
        this._closeOryn();
      }

      if (this._isShowreelPlaying) {
        this._stopShowreel();
      }

      if (this._isGameActive) {
        this._hideGame();
      }
    }

    if (partType === MONITOR_PART_TYPE.MonitorScreenYoutubeLogo) {
      if (this._isShowreelPlaying) {
        this._onOpenYoutube();
      }
    }
  }

  _playShowreel() {
    if (this._isGameActive) {
      return;
    }

    this._isShowreelPlaying = true;
    this._onFullScreenEnabled();

    this._parts[MONITOR_PART_TYPE.MonitorScreen].material.map = this._showreelTexture;
    this._parts[MONITOR_PART_TYPE.MonitorScreenYoutubeLogo].visible = true;
    this._parts[MONITOR_PART_TYPE.MonitorScreenYoutubeLogo].scale.set(1, 1, 1);

    this._showreelVideoElement.play();

    this._debugMenu.updateShowreelButton(this._isShowreelPlaying);
    this._debugMenu.disableGameButton();
    this.events.post('onShowreelStart');
  }

  _stopShowreel() {
    this._isShowreelPlaying = false;
    this._isShowreelPaused = false;
    this._onFullScreenDisabled();

    this._parts[MONITOR_PART_TYPE.MonitorScreen].material.map = this._screenTexture;
    this._parts[MONITOR_PART_TYPE.MonitorScreenYoutubeLogo].visible = false;
    this._parts[MONITOR_PART_TYPE.MonitorScreenYoutubeLogo].scale.set(0, 0, 0);

    this._showreelVideoElement.pause();
    this._showreelVideoElement.currentTime = 0;

    this._debugMenu.updateShowreelButton(this._isShowreelPlaying);
    this._debugMenu.enableGameButton();
    this.events.post('onShowreelStop');
  }

  _onFullScreenEnabled() {
    const showreelIcon = this._parts[MONITOR_PART_TYPE.MonitorScreenShowreelIcon];      // ORYN (top-left)
    const gameBoyIcon = this._parts[MONITOR_PART_TYPE.MonitorScreenTransferItIcon];     // Game Boy (top-middle, repurposed mesh)
    const issIcon = this._parts[MONITOR_PART_TYPE.MonitorScreenGameBoyIcon];            // DOCK / ISS sim (top-right, repurposed mesh)
    const closeIcon = this._parts[MONITOR_PART_TYPE.MonitorScreenCloseIcon];

    closeIcon.visible = true;
    closeIcon.position.z += MONITOR_CONFIG.hideOffset;
    showreelIcon.visible = false;
    showreelIcon.position.z -= MONITOR_CONFIG.hideOffset;
    
    gameBoyIcon.visible = false;
    gameBoyIcon.position.z -= MONITOR_CONFIG.hideOffset;
    if (issIcon) {
      issIcon.visible = false;
      issIcon.position.z -= MONITOR_CONFIG.hideOffset;
    }
    // Bottom row is fully hidden — skip.
  }

  _onFullScreenDisabled() {
    const showreelIcon = this._parts[MONITOR_PART_TYPE.MonitorScreenShowreelIcon];      // ORYN (top-left)
    const gameBoyIcon = this._parts[MONITOR_PART_TYPE.MonitorScreenTransferItIcon];     // Game Boy (top-middle, repurposed mesh)
    const issIcon = this._parts[MONITOR_PART_TYPE.MonitorScreenGameBoyIcon];            // DOCK / ISS sim (top-right, repurposed mesh)
    const closeIcon = this._parts[MONITOR_PART_TYPE.MonitorScreenCloseIcon];

    closeIcon.visible = false;
    closeIcon.position.z -= MONITOR_CONFIG.hideOffset;
    showreelIcon.visible = true;
    showreelIcon.position.z += MONITOR_CONFIG.hideOffset;
    
    gameBoyIcon.visible = true;
    gameBoyIcon.position.z += MONITOR_CONFIG.hideOffset;
    if (issIcon) {
      issIcon.visible = true;
      issIcon.position.z += MONITOR_CONFIG.hideOffset;
    }
    // Bottom row stays hidden — do not re-show.
  }

  // Three-icon layout (top: ORYN / Game Boy / DOCK · bottom: all hidden).
  // NASA Eyes removed to keep the monitor focused — one space wink (DOCK) is
  // intentional; two becomes a theme. CrazyPumpkin & HexCastle were always dead.
  _hideDeadIcons() {
    [
      MONITOR_PART_TYPE.MonitorScreenCrazyPumpkinIcon,
      MONITOR_PART_TYPE.MonitorScreenCubeScapeIcon,
      MONITOR_PART_TYPE.MonitorScreenHexCastleIcon,
    ].forEach((partType) => {
      const mesh = this._parts[partType];
      if (mesh) mesh.visible = false;
    });
  }

  _pauseShowreel() {
    this._isShowreelPaused = true;

    this._showreelVideoElement.pause();
    this.events.post('onShowreelPause');
  }

  _resumeShowreel() {
    this._isShowreelPaused = false;

    this._showreelVideoElement.play();
    this.events.post('onShowreelPause');
  }

  // ORYN icon click -> zoom camera to monitor + overlay ORYN site iframe over the screen.
  // Open any URL embedded in the monitor screen (ORYN, Game Boy, etc.)
  _openInScreen(url, popoutOptions = null) {
    this._isOrynActive = true;
    this._currentOverlayUrl = url;
    this._currentPopoutOptions = popoutOptions;
    this._onFullScreenEnabled();
    this.events.post('onMonitorScreenClick');
    this._showOrynOverlay(url);
  }

  _closeOryn() {
    this._isOrynActive = false;
    this._hideOrynOverlay();
    // Restore the screen's app icons but stay focused on the monitor —
    // user can pick another app instead of being thrown back to the desk.
    this._onFullScreenDisabled();
  }

  _showOrynOverlay(url) {
    if (this._orynIframe) return;

    // Defensive: clean up any stale leftovers from a previous open.
    document.querySelectorAll('#oryn-screen-frame, #oryn-screen-close, #oryn-screen-popout').forEach(el => el.remove());

    const iframe = document.createElement('iframe');
    iframe.id = 'oryn-screen-frame';
    iframe.src = url;
    iframe.setAttribute('allow', 'fullscreen');
    iframe.style.cssText = ORYN_IFRAME_BASE_STYLE;
    document.body.appendChild(iframe);

    this._orynIframe = iframe;

    // Pop-out CTA button — floats over the iframe, opens the real URL in a new tab.
    const popoutOpts = this._currentPopoutOptions;
    if (popoutOpts) {
      const popout = document.createElement('button');
      popout.id = 'oryn-screen-popout';
      popout.textContent = popoutOpts.label || 'Open in new tab ↗';
      popout.style.cssText = `
        position: fixed; z-index: 51; opacity: 0;
        background: rgba(20,20,28,0.92); color: #ffffff;
        border: 1px solid rgba(255,255,255,0.18); border-radius: 6px;
        padding: 8px 14px; font: 600 13px/1 -apple-system, "Helvetica Neue", Arial, sans-serif;
        letter-spacing: 0.3px; cursor: pointer;
        transition: opacity 0.35s ease, transform 0.15s ease, background 0.15s ease;
        box-shadow: 0 4px 16px rgba(0,0,0,0.4);
        user-select: none;
      `;
      popout.onmouseenter = () => {
        popout.style.background = 'rgba(30,30,42,1)';
        popout.style.transform = 'translateY(-1px)';
      };
      popout.onmouseleave = () => {
        popout.style.background = 'rgba(20,20,28,0.92)';
        popout.style.transform = 'translateY(0)';
      };
      popout.onclick = () => {
        const ev = popoutOpts.trackEvent;
        if (ev) window.track && window.track(ev);
        this._showFullscreenModal(url);
      };
      document.body.appendChild(popout);
      this._orynPopout = popout;
    }

    // Position once, then track every frame so the iframe follows the screen.
    this._orynTrackTick();
    requestAnimationFrame(() => {
      iframe.style.opacity = '1';
      if (this._orynPopout) this._orynPopout.style.opacity = '1';
    });

    const loop = () => {
      if (!this._isOrynActive) return;
      this._orynTrackTick();
      this._orynRafId = requestAnimationFrame(loop);
    };
    this._orynRafId = requestAnimationFrame(loop);
  }

  // Full-viewport in-page modal — visitors stay on ricvasquez.com instead of
  // opening external content in a new tab. Closed via the "back to the office"
  // button or ESC key.
  _showFullscreenModal(url) {
    document.querySelectorAll('#room-fullscreen-modal').forEach(el => el.remove());

    const modal = document.createElement('div');
    modal.id = 'room-fullscreen-modal';
    modal.style.cssText = `
      position: fixed; inset: 0; z-index: 999999;
      background: #0a0a0e;
      opacity: 0; transition: opacity 0.25s ease;
    `;

    const iframe = document.createElement('iframe');
    iframe.src = url;
    iframe.setAttribute('allow', 'fullscreen; autoplay; clipboard-read; clipboard-write; microphone; camera');
    iframe.style.cssText = `
      position: absolute; inset: 0;
      width: 100%; height: 100%;
      border: 0;
    `;
    modal.appendChild(iframe);

    const close = document.createElement('button');
    close.textContent = '← back to the office';
    close.style.cssText = `
      position: absolute; top: 16px; left: 16px; z-index: 2;
      background: rgba(20,20,28,0.9); color: #fff;
      border: 1px solid rgba(255,255,255,0.18); border-radius: 6px;
      padding: 9px 14px; font: 600 13px/1 -apple-system, "Helvetica Neue", Arial, sans-serif;
      letter-spacing: 0.3px; cursor: pointer;
      transition: background 0.15s ease, transform 0.15s ease;
      -webkit-backdrop-filter: blur(8px);
      backdrop-filter: blur(8px);
    `;
    close.onmouseenter = () => {
      close.style.background = 'rgba(30,30,42,1)';
      close.style.transform = 'translateY(-1px)';
    };
    close.onmouseleave = () => {
      close.style.background = 'rgba(20,20,28,0.9)';
      close.style.transform = 'translateY(0)';
    };

    const closeModal = () => {
      modal.style.opacity = '0';
      window.track && window.track('monitor_fullscreen_closed', { url });
      setTimeout(() => modal.remove(), 250);
      document.removeEventListener('keydown', escHandler);
    };
    const escHandler = (e) => { if (e.key === 'Escape') closeModal(); };

    close.onclick = closeModal;
    document.addEventListener('keydown', escHandler);
    modal.appendChild(close);

    document.body.appendChild(modal);
    requestAnimationFrame(() => { modal.style.opacity = '1'; });
  }

  _hideOrynOverlay() {
    if (this._orynRafId) {
      cancelAnimationFrame(this._orynRafId);
      this._orynRafId = null;
    }
    if (this._orynIframe) {
      const f = this._orynIframe;
      f.style.opacity = '0';
      setTimeout(() => f.remove(), 350);
      this._orynIframe = null;
    }
    if (this._orynPopout) {
      const p = this._orynPopout;
      p.style.opacity = '0';
      setTimeout(() => p.remove(), 350);
      this._orynPopout = null;
    }
  }

  // Project the monitor screen mesh's 8 bbox corners through the camera
  // and size/position the iframe to that on-screen rect every frame.
  _orynTrackTick() {
    const camera = window.__roomCamera;
    const screenMesh = this._parts[MONITOR_PART_TYPE.MonitorScreen];
    if (!camera || !screenMesh || !this._orynIframe) return;

    // Auto-close if the camera's intended focus is no longer the monitor.
    // We only check focusObjectType (set immediately on focusCamera()), not mode —
    // mode is briefly NoControls during the transition animation, which would
    // incorrectly close the overlay the instant the user opens it.
    if (CAMERA_CONFIG.focusObjectType !== CAMERA_FOCUS_OBJECT_TYPE.Monitor) {
      this._closeOryn();
      return;
    }

    screenMesh.updateWorldMatrix(true, false);
    if (!screenMesh.geometry.boundingBox) {
      screenMesh.geometry.computeBoundingBox();
    }
    const bb = screenMesh.geometry.boundingBox;
    const corners = [
      new THREE.Vector3(bb.min.x, bb.min.y, bb.min.z),
      new THREE.Vector3(bb.min.x, bb.min.y, bb.max.z),
      new THREE.Vector3(bb.min.x, bb.max.y, bb.min.z),
      new THREE.Vector3(bb.min.x, bb.max.y, bb.max.z),
      new THREE.Vector3(bb.max.x, bb.min.y, bb.min.z),
      new THREE.Vector3(bb.max.x, bb.min.y, bb.max.z),
      new THREE.Vector3(bb.max.x, bb.max.y, bb.min.z),
      new THREE.Vector3(bb.max.x, bb.max.y, bb.max.z),
    ];

    const w = window.innerWidth;
    const h = window.innerHeight;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    for (const c of corners) {
      c.applyMatrix4(screenMesh.matrixWorld).project(camera);
      const sx = (c.x * 0.5 + 0.5) * w;
      const sy = (-c.y * 0.5 + 0.5) * h;
      if (sx < minX) minX = sx;
      if (sy < minY) minY = sy;
      if (sx > maxX) maxX = sx;
      if (sy > maxY) maxY = sy;
    }

    const screenW = Math.max(0, maxX - minX);
    const screenH = Math.max(0, maxY - minY);
    if (screenW < 20 || screenH < 20) return;

    // Inset within the screen rect (leaves menu bar + dock visible around the iframe)
    const left   = minX + screenW * ORYN_INSET.left;
    const top    = minY + screenH * ORYN_INSET.top;
    const width  = screenW * (1 - ORYN_INSET.left - ORYN_INSET.right);
    const height = screenH * (1 - ORYN_INSET.top - ORYN_INSET.bottom);

    // Scale trick: render the iframe at a comfortable logical width and
    // visually scale it down to fit. ORYN's responsive layout looks right.
    const logicalW = ORYN_LOGICAL_WIDTH;
    const logicalH = logicalW * (height / width);
    const scale    = width / logicalW;

    const f = this._orynIframe;
    f.style.left   = `${left}px`;
    f.style.top    = `${top}px`;
    f.style.width  = `${logicalW}px`;
    f.style.height = `${logicalH}px`;
    f.style.transform = `scale(${scale})`;

    // Position the pop-out CTA button below the iframe, centered horizontally
    if (this._orynPopout) {
      const p = this._orynPopout;
      // Measure after first render so width is real
      const btnW = p.offsetWidth || 160;
      const btnH = p.offsetHeight || 36;
      p.style.left = `${left + width / 2 - btnW / 2}px`;
      p.style.top  = `${top + height + 12}px`;
    }
  }

  _openCrazyPumpkinProject() {
    if (SCENE_CONFIG.isMobile) {
      const linkElement = document.getElementById('crazy_pumpkin_link');
      linkElement.setAttribute('href', MONITOR_LINKS_CONFIG.crazyPumpkin);
      linkElement.click();
    } else {
      window.open(MONITOR_LINKS_CONFIG.crazyPumpkin, '_blank').focus();
    }
  }

  _openCubeScapeProject() {
    if (SCENE_CONFIG.isMobile) {
      const linkElement = document.getElementById('cubescape_link');
      linkElement.setAttribute('href', MONITOR_LINKS_CONFIG.cubeScape);
      linkElement.click();
    } else {
      window.open(MONITOR_LINKS_CONFIG.cubeScape, '_blank').focus();
    }
  }

  _openHexCastleProject() {
    if (SCENE_CONFIG.isMobile) {
      const linkElement = document.getElementById('hexcastle_link');
      linkElement.setAttribute('href', MONITOR_LINKS_CONFIG.hexCastle);
      linkElement.click();
    } else {
      window.open(MONITOR_LINKS_CONFIG.hexCastle, '_blank').focus();
    }
  }

  _onOpenYoutube() {
    if (SCENE_CONFIG.isMobile) {
      const linkElement = document.getElementById('youtube_link');
      linkElement.setAttribute('href', MONITOR_LINKS_CONFIG.showreel);
      linkElement.click();
    } else {
      window.open(MONITOR_LINKS_CONFIG.showreel, '_blank').focus();
    }
  }

  _updatePosition() {
    MONITOR_CONFIG.positionZ = this._currentPositionZ - this._parts[MONITOR_PART_TYPE.Monitor].userData.startPosition.z;
    this._debugMenu.updatePosition();
  }

  _updateMonitorPosition(deltaZ) {
    const screen = this._parts[MONITOR_PART_TYPE.MonitorScreen];

    this._screenGroup.position.z = screen.userData.startPosition.z + deltaZ;
  }

  _updateArmMount(deltaZ) {
    const monitor = this._parts[MONITOR_PART_TYPE.Monitor];
    const arm01 = this._parts[MONITOR_PART_TYPE.MonitorArmMountArm01];
    const arm02 = this._parts[MONITOR_PART_TYPE.MonitorArmMountArm02];

    arm01.rotation.y = arm01.userData.startAngle.y - deltaZ * MONITOR_ARM_MOUNT_CONFIG.arm01.angleCoeff;
    arm02.rotation.y = arm02.userData.startAngle.y - deltaZ * MONITOR_ARM_MOUNT_CONFIG.arm02.angleCoeff;

    arm02.position.x = arm01.position.x + Math.cos(arm01.rotation.y + Math.PI * 0.5) * MONITOR_ARM_MOUNT_CONFIG.arm01.shoulderCoeff;
    arm02.position.z = arm01.position.z + Math.sin(arm01.rotation.y + Math.PI * 0.5) * MONITOR_ARM_MOUNT_CONFIG.arm01.shoulderCoeff;

    const bonusAngle = MONITOR_ARM_MOUNT_CONFIG.arm02.bonusAngle * THREE.MathUtils.DEG2RAD;
    const positionX = arm02.position.x + Math.cos(-arm02.rotation.y - bonusAngle + Math.PI * 0.5) * MONITOR_ARM_MOUNT_CONFIG.arm02.shoulderCoeff;
    monitor.position.x = this._screenGroup.position.x = positionX;

    this._updateArmRotation();
    this._debugMenu.updateArmRotation();
  }

  _updateArmRotation() {
    const arm01 = this._parts[MONITOR_PART_TYPE.MonitorArmMountArm01];
    const arm02 = this._parts[MONITOR_PART_TYPE.MonitorArmMountArm02];

    MONITOR_ARM_MOUNT_CONFIG.arm01.angle = Math.round(arm01.rotation.y * THREE.MathUtils.RAD2DEG * 100) / 100;
    MONITOR_ARM_MOUNT_CONFIG.arm02.angle = -Math.round(arm02.rotation.y * THREE.MathUtils.RAD2DEG * 100) / 100;
  }

  _updateHelpArrows(deltaZ) {
    this._helpArrows.position.z = this._parts[MONITOR_PART_TYPE.Monitor].userData.startPosition.z + deltaZ;
    this._helpArrows.position.x = this._parts[MONITOR_PART_TYPE.Monitor].position.x;
  }

  _showVolume(currentVolume) {
    const volumePart = this._parts[MONITOR_PART_TYPE.MonitorScreenVolume];
    volumePart.visible = true;

    volumePart.material.uniforms.uAlpha.value = 1;
    volumePart.material.uniforms.uRectsCount.value = currentVolume;

    if (this._volumeTween) {
      this._volumeTween.stop();
    }

    this._volumeTween = new TWEEN.Tween(volumePart.material.uniforms.uAlpha)
      .to({ value: 0 }, 600)
      .easing(TWEEN.Easing.Sinusoidal.Out)
      .delay(1000)
      .start()
      .onComplete(() => {
        volumePart.visible = false;
      });
  }

  _init() {
    this._initParts();
    this._addMaterials();
    this._addPartsToScene();
    this._initGroups();
    this._initCloseFocusIcon();
    this._initFocusObject();
    this._initScreenTextures();
    this._updateArmRotation();
    this._initArrows();
    this._initDebugMenu();
    this._initYoutubeLink();
    this._initGameBoyLink();
    this._hideDeadIcons();
    this._initCrazyPumpkinLink();
    this._initCubeScapeLink();
    this._initHexCastleLink();
    this._initSignals();

    this.resetToInitState();
  }

  _addMaterials() {
    const material = Materials.getMaterial(Materials.type.bakedBigObjects);

    for (const partName in this._parts) {
      const part = this._parts[partName];
      part.material = material;
    }
  }

  _initGroups() {
    const screenGroup = this._screenGroup = new THREE.Group();
    this.add(screenGroup);

    const monitorScreen = this._parts[MONITOR_PART_TYPE.MonitorScreen];
    const monitorScreenShowreelIcon = this._parts[MONITOR_PART_TYPE.MonitorScreenShowreelIcon];
    const monitorScreenTransferItIcon = this._parts[MONITOR_PART_TYPE.MonitorScreenTransferItIcon];
    const monitorScreenGameBoyIcon = this._parts[MONITOR_PART_TYPE.MonitorScreenGameBoyIcon];
    const monitorScreenCrazyPumpkinIcon = this._parts[MONITOR_PART_TYPE.MonitorScreenCrazyPumpkinIcon];
    const monitorScreenCubeScapeIcon = this._parts[MONITOR_PART_TYPE.MonitorScreenCubeScapeIcon];
    const monitorScreenHexCastleIcon = this._parts[MONITOR_PART_TYPE.MonitorScreenHexCastleIcon];
    const monitorScreenCloseIcon = this._parts[MONITOR_PART_TYPE.MonitorScreenCloseIcon];
    const monitorScreenVolume = this._parts[MONITOR_PART_TYPE.MonitorScreenVolume];
    const monitorScreenYoutubeIcon = this._parts[MONITOR_PART_TYPE.MonitorScreenYoutubeLogo];
    const monitorCloseFocusIcon = this._parts[MONITOR_PART_TYPE.MonitorCloseFocusIcon];
    screenGroup.add(monitorScreen, monitorScreenShowreelIcon, monitorScreenTransferItIcon, monitorScreenCloseIcon, monitorScreenYoutubeIcon, monitorScreenVolume, monitorCloseFocusIcon, monitorScreenGameBoyIcon, monitorScreenCrazyPumpkinIcon, monitorScreenCubeScapeIcon, monitorScreenHexCastleIcon);

    screenGroup.position.copy(monitorScreen.position);

    const showreelIconOffset = monitorScreenShowreelIcon.position.clone().sub(monitorScreen.position.clone());
    const transferItIconOffset = monitorScreenTransferItIcon.position.clone().sub(monitorScreen.position.clone());
    const gameBoyIconOffset = monitorScreenGameBoyIcon.position.clone().sub(monitorScreen.position.clone());
    const crazyPumpkinIconOffset = monitorScreenCrazyPumpkinIcon.position.clone().sub(monitorScreen.position.clone());
    const cubeScapeIconOffset = monitorScreenCubeScapeIcon.position.clone().sub(monitorScreen.position.clone());
    const hexCastleIconOffset = monitorScreenHexCastleIcon.position.clone().sub(monitorScreen.position.clone());
    const closeIconOffset = monitorScreenCloseIcon.position.clone().sub(monitorScreen.position.clone());
    const volumeOffset = monitorScreenVolume.position.clone().sub(monitorScreen.position.clone());
    const closeFocusIconOffset = monitorCloseFocusIcon.position.clone().sub(monitorScreen.position.clone());
    const monitorScreenYoutubeIconOffset = monitorScreenYoutubeIcon.position.clone().sub(monitorScreen.position.clone());

    monitorScreen.position.set(0, 0, 0);
    monitorScreenShowreelIcon.position.copy(showreelIconOffset);
    monitorScreenTransferItIcon.position.copy(transferItIconOffset);
    monitorScreenGameBoyIcon.position.copy(gameBoyIconOffset);
    monitorScreenCrazyPumpkinIcon.position.copy(crazyPumpkinIconOffset);
    monitorScreenCubeScapeIcon.position.copy(cubeScapeIconOffset);
    monitorScreenHexCastleIcon.position.copy(hexCastleIconOffset);
    monitorScreenCloseIcon.position.copy(closeIconOffset);
    monitorScreenCloseIcon.position.z -= MONITOR_CONFIG.hideOffset;
    monitorScreenVolume.position.copy(volumeOffset);
    monitorCloseFocusIcon.position.copy(closeFocusIconOffset);
    monitorScreenYoutubeIcon.position.copy(monitorScreenYoutubeIconOffset);
  }

  _initFocusObject() {
    const focusObject = this._focusObject = new THREE.Object3D();
    this.add(focusObject);

    const monitor = this._parts[MONITOR_PART_TYPE.Monitor];
    focusObject.position.copy(monitor.position);
  }

  _initScreenTextures() {
    this._initScreenTexture();
    this._initButtonsTextures();
    this._initVolumeTexture();
    this._initShowreelVideo();
  }

  _initScreenTexture() {
    const texture = this._screenTexture = Loader.assets['screens/monitor/monitor-screen'];
    // texture.colorSpace = THREE.SRGBColorSpace;

    const material = new THREE.MeshBasicMaterial({
      map: texture,
    });

    const monitorScreen = this._parts[MONITOR_PART_TYPE.MonitorScreen];
    monitorScreen.material = material;
  }

  _initButtonsTextures() {
    const sparkleConfig = SPARKLE_CONFIG[MONITOR_TYPE.Monitor];

    MONITOR_SCREEN_BUTTONS.forEach((partType, i) => {
      const part = this._parts[partType];
      const textureName = MONITOR_BUTTONS_CONFIG.buttons[partType].textureName;
      const texture = Loader.assets[textureName];
      // texture.colorSpace = THREE.SRGBColorSpace;

      const uniforms = {
        uTime: { value: 0 },
        uStartOffset: { value: i / MONITOR_SCREEN_BUTTONS.length },
        uTexture: { value: texture },
        uColor: { value: new THREE.Color(0xffffff) },
        uSparkleColor: { value: sparkleConfig.color },
        uLineThickness: { value: sparkleConfig.thickness },
        uBlurAmount: { value: sparkleConfig.blur },
        uLineAngle: { value: sparkleConfig.angle * THREE.MathUtils.DEG2RAD },
        uSpeed: { value: sparkleConfig.speed },
        uLineMovingWidth: { value: sparkleConfig.movingWidth },
      };

      part.material = new THREE.ShaderMaterial({
        uniforms,
        vertexShader: sparkleVertexShader,
        fragmentShader: sparkleFragmentShader,
      });
    });

    this._parts[MONITOR_PART_TYPE.MonitorScreenCloseIcon].visible = false;
    this._parts[MONITOR_PART_TYPE.MonitorScreenYoutubeLogo].visible = false;
    this._parts[MONITOR_PART_TYPE.MonitorScreenYoutubeLogo].scale.set(0, 0, 0);
  }

  _initVolumeTexture() {
    const volumePart = this._parts[MONITOR_PART_TYPE.MonitorScreenVolume];
    this._volumeIcon = new VolumeIcon(volumePart);
  }

  _initShowreelVideo() {
    const videoElement = this._showreelVideoElement = document.createElement('video');
    videoElement.muted = true;
    videoElement.controls = true;
    videoElement.playsInline = true;
    videoElement.src = '/video/games_showreel.mp4';

    videoElement.addEventListener('ended', () => {
      this._stopShowreel();
    });

    this._showreelTexture = new THREE.VideoTexture(videoElement);
    // texture.colorSpace = THREE.SRGBColorSpace;
  }

  _initArrows() {
    const helpArrowsTypes = [HELP_ARROW_TYPE.MonitorBack, HELP_ARROW_TYPE.MonitorFront];
    const helpArrows = this._helpArrows = new HelpArrows(helpArrowsTypes);
    this.add(helpArrows);

    helpArrows.position.copy(this._parts[MONITOR_PART_TYPE.Monitor].position.clone());
  }

  _initYoutubeLink() {
    if (SCENE_CONFIG.isMobile) {
      const linkElement = document.createElement('a');
      linkElement.id = 'youtube_link';
      window.document.body.appendChild(linkElement);
    }
  }

  _initGameBoyLink() {
    if (SCENE_CONFIG.isMobile) {
      const linkElement = document.createElement('a');
      linkElement.id = 'gameboy_link';
      window.document.body.appendChild(linkElement);
    }
  }

  _initCrazyPumpkinLink() {
    if (SCENE_CONFIG.isMobile) {
      const linkElement = document.createElement('a');
      linkElement.id = 'crazy_pumpkin_link';
      window.document.body.appendChild(linkElement);
    }
  }

  _initCubeScapeLink() {
    if (SCENE_CONFIG.isMobile) {
      const linkElement = document.createElement('a');
      linkElement.id = 'cubescape_link';
      window.document.body.appendChild(linkElement);
    }
  }

  _initHexCastleLink() {
    if (SCENE_CONFIG.isMobile) {
      const linkElement = document.createElement('a');
      linkElement.id = 'hexcastle_link';
      window.document.body.appendChild(linkElement);
    }
  }

  _initCloseFocusIcon() {
    const texture = Loader.assets['close-icon'];
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(2, 2);

    const material = new THREE.MeshBasicMaterial({
      map: texture,
    });

    const closeFocusIcon = this._parts[MONITOR_PART_TYPE.MonitorCloseFocusIcon];
    closeFocusIcon.material = material;

    closeFocusIcon.visible = false;
  }

  _initSignals() {
    this._debugMenu.events.on('onPositionChanged', (msg, position) => this._onPositionChanged(position));
    this._debugMenu.events.on('onPlayShowreelVideo', () => this._onDebugPlayShowreelVideo());
    this._debugMenu.events.on('onShowGame', () => this._onShowGame());
  }

  _onPositionChanged(position) {
    this._currentPositionZ = position + this._parts[MONITOR_PART_TYPE.Monitor].userData.startPosition.z;
  }

  _onDebugPlayShowreelVideo() {
    if (this._isShowreelPlaying) {
      this._stopShowreel();
    } else {
      this._playShowreel();
    }
  }

  _onShowGame() {
    if (this._isShowreelPlaying) {
      return;
    }

    if (this._isGameActive) {
      this._hideGame();
    } else {
      this.startGame();
    }
  }

  startGame() {
    this._isGameActive = true;
    this._onFullScreenEnabled();

    const monitorScreen = this._parts[MONITOR_PART_TYPE.MonitorScreen];
    monitorScreen.material.map = this._sceneRenderTexture;

    this._debugMenu.updateGameButton(this._isGameActive);
    this._debugMenu.disableShowreelButton();
    this.events.post('onShowGame');
  }

  _hideGame() {
    this._isGameActive = false;
    this._isScreenActiveForGame = false;
    this._onFullScreenDisabled();

    const monitorScreen = this._parts[MONITOR_PART_TYPE.MonitorScreen];
    monitorScreen.material.map = this._screenTexture;

    this._debugMenu.updateGameButton(this._isGameActive);
    this._debugMenu.enableShowreelButton();
    this.events.post('onHideGame');
  }

  _getPartsWithoutButtons() {
    const parts = [];

    MONITOR_PARTS_WITHOUT_BUTTONS.forEach((partName) => {
      parts.push(this._parts[partName]);
    });

    return parts;
  }
}
